/** Categories prevues par le format, dans l'ordre des prefixes de fichiers. */
export const CATEGORIES = [1, 2, 3, 4, 5, 6, 7];
/**
 * Tout est coche par defaut : une partie tire dans l'ensemble du catalogue tant
 * que personne n'a restreint. Retirer est un geste, pas l'etat initial.
 */
export const DEFAULT_CATEGORIES = CATEGORIES;
export function isCategory(value) {
    return CATEGORIES.includes(value);
}
/**
 * Valide une selection : sous-ensemble NON VIDE de CATEGORIES, sans doublon.
 *
 * Le vide est refuse ici, au niveau du protocole, et pas seulement grise dans
 * l'interface : une partie sans categorie n'a aucun tableau a tirer. L'interface
 * empeche le geste, le protocole garantit le resultat.
 *
 * L'existence de contenu dans chaque categorie est verifiee ailleurs (cote
 * serveur), ce module ne connaissant pas le catalogue.
 */
export function isValidCategorySelection(value) {
    if (!Array.isArray(value) || value.length === 0)
        return false;
    if (!value.every(isCategory))
        return false;
    return new Set(value).size === value.length;
}
function shuffle(items, random) {
    const copy = [...items];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(random() * (i + 1));
        const a = copy[i];
        copy[i] = copy[j];
        copy[j] = a;
    }
    return copy;
}
/**
 * Repartit `rounds` manches entre les categories cochees.
 *
 * Trois proprietes :
 *  1. la repartition est la plus egale possible (au plus 1 manche d'ecart), de
 *     sorte qu'une categorie cochee soit reellement jouee ;
 *  2. le surplus est TIRE AU SORT ;
 *  3. deux manches de suite ne tombent jamais dans la meme categorie, tant que
 *     c'est arithmetiquement possible.
 *
 * Le tirage n'est pas un ornement. L'ancienne version servait les niveaux dans
 * l'ordre croissant et donnait le surplus aux plus bas : applique a des
 * categories, cela jouait toujours la meme sequence, et surtout une partie de
 * 3 manches sur 5 categories cochees ne montrait JAMAIS les deux dernieres.
 * Cocher « Art » sans jamais voir un tableau d'art est un mensonge de reglage.
 *
 * La troisieme propriete n'est pas cosmetique : un simple melange laisse
 * regulierement deux manches identiques cote a cote, et l'on a alors le
 * sentiment de rejouer la meme chose. Avoir coche plusieurs categories doit se
 * VOIR d'une manche a la suivante. Elle est impossible avec une seule categorie
 * cochee, et la fonction ne la force alors pas.
 */
export function computeRoundCategories(rounds, categories, random = Math.random) {
    if (!Number.isInteger(rounds) || rounds <= 0)
        return [];
    // Dedoublonne : l'appelant peut fournir n'importe quel ordre.
    const unique = [...new Set(categories)];
    if (unique.length === 0)
        return [];
    // L'ordre tire decide qui prend le surplus, donc quelles categories sont
    // servies quand il y a moins de manches que de categories.
    const drawn = shuffle(unique, random);
    const base = Math.floor(rounds / drawn.length);
    const surplus = rounds % drawn.length;
    const remaining = new Map();
    for (const [index, category] of drawn.entries()) {
        remaining.set(category, base + (index < surplus ? 1 : 0));
    }
    // Ordonnancement glouton : a chaque manche on prend la categorie qui a le
    // plus de manches en attente PARMI celles qui ne sont pas celle d'avant. Un
    // melange simple ne peut pas garantir l'alternance ; prendre la plus fournie
    // si, car c'est elle qui risquerait de se retrouver coincee en fin de liste.
    const plan = [];
    let previous = null;
    for (let round = 0; round < rounds; round += 1) {
        const left = [...remaining.entries()].filter(([, count]) => count > 0);
        if (left.length === 0)
            break;
        // Si la seule categorie restante est celle d'avant, on la reprend : mieux
        // vaut une repetition qu'une manche sans tableau.
        const others = left.filter(([category]) => category !== previous);
        const pool = others.length > 0 ? others : left;
        const most = Math.max(...pool.map(([, count]) => count));
        const best = pool.filter(([, count]) => count === most);
        const [category] = best[Math.floor(random() * best.length)];
        plan.push(category);
        remaining.set(category, remaining.get(category) - 1);
        previous = category;
    }
    return plan;
}
//# sourceMappingURL=categories.js.map